import BaseCrudService from './BaseCrudService';
import api from './api';

class QuickBooksService extends BaseCrudService {
  constructor() {
    super('/api/quickbooks');
  }

  // Configuration
  getConfig() {
    return this.customRequest('get', 'config');
  }

  // Authorization
  startAuthorization() {
    return this.customRequest('get', 'authorize');
  }

  disconnect() {
    return this.customRequest('post', 'disconnect');
  }

  testConnection() {
    return this.customRequest('get', 'test-connection');
  }

  // Sync
  syncTransactions(options = {}) {
    return this.customRequest('post', 'sync', options);
  }

  getSyncStats() {
    return this.customRequest('get', 'sync-stats');
  }

  updateSettings(settings) {
    return this.customRequest('put', 'settings', settings);
  }

  // Transactions
  getTransactions(params = {}) {
    return this.customRequest('get', 'transactions', null, { params });
  }

  getUnmatchedTransactions(params = {}) {
    return this.customRequest('get', 'transactions/unmatched', null, { params });
  }

  matchTransaction(transactionId, receiptId, notes = '') {
    return this.customRequest('post', `transactions/${transactionId}/match`, {
      receiptId,
      notes
    });
  }

  unmatchTransaction(transactionId) {
    return this.customRequest('post', `transactions/${transactionId}/unmatch`);
  }

  getReceiptsForReconciliation(params = {}) {
    return this.customRequest('get', 'receipts', null, { params });
  }

  getMatchedPairs(params = {}) {
    return this.customRequest('get', 'matched-pairs', null, { params });
  }

  // Receipt upload
  uploadReceipt(receiptId, transactionId) {
    return this.customRequest('post', 'upload-receipt', {
      receiptId,
      transactionId
    });
  }

  // Enhanced features
  getDashboardSummary(days = 30) {
    return this.customRequest('get', 'dashboard', null, {
      params: { days }
    });
  }

  getReferenceData() {
    return this.customRequest('get', 'reference-data');
  }

  getMatchSuggestions(transactionId) {
    return this.customRequest('get', `transactions/${transactionId}/suggestions`);
  }

  bulkMatchTransactions(matches) {
    return this.customRequest('post', 'transactions/bulk-match', {
      matches
    });
  }

  createExpenseFromReceipt(receiptId, expenseData) {
    return this.customRequest('post', 'create-expense', {
      receiptId,
      expenseData
    });
  }

  getAnalytics(days = 30) {
    return this.customRequest('get', 'analytics', null, {
      params: { days }
    });
  }

  // Sync matched receipt to QuickBooks
  syncMatchedReceipt(transactionId) {
    return this.customRequest('post', `transactions/${transactionId}/sync-receipt`);
  }

  // Bulk sync matched receipts to QuickBooks
  bulkSyncMatchedReceipts(transactionIds) {
    return this.customRequest('post', 'sync-matched-receipts', {
      transactionIds
    });
  }

  // Manual transaction management
  createTransaction(transactionData) {
    return this.customRequest('post', 'transactions', transactionData);
  }

  updateTransaction(transactionId, transactionData) {
    return this.customRequest('put', `transactions/${transactionId}`, transactionData);
  }

  deleteTransaction(transactionId) {
    return this.customRequest('delete', `transactions/${transactionId}`);
  }

  // Transaction upload
  uploadTransactionsCSV(csvData, financeAccount = null) {
    return this.customRequest('post', 'transactions/upload-csv', { csvData, financeAccount });
  }

  uploadTransactionsFile(file, financeAccount = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (financeAccount) {
      formData.append('financeAccount', financeAccount);
    }

    return this.request(
      () => api.post('/api/quickbooks/transactions/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
      'Failed to upload transactions file'
    );
  }

  // Account Mapping
  getAccountMappings() {
    return this.customRequest('get', 'account-mappings');
  }

  saveAccountMappings(mappings) {
    return this.customRequest('post', 'account-mappings', { mappings });
  }

  // Detailed Sync Info
  getSyncInfo() {
    return this.customRequest('get', 'sync-info');
  }
}

export default new QuickBooksService();